class FocalInfiniteScroll {
  constructor(options = {}) {
    this.options = {
      container: '#infinite-scroll-container',
      productList: '#product-grid',
      pagination: '.infinite-pagination',
      loadingText: 'Loading more products...',
      threshold: 200,
      maxPages: 50,
      enablePreloader: true,
      ...options
    };
    
    this.isLoading = false;
    this.currentPage = 1;
    this.maxPagesReached = false;
    this.controller = new AbortController();
    
    this.init();
  }

  init() {
    this.container = document.querySelector(this.options.container);
    this.productList = document.querySelector(this.options.productList);
    this.pagination = document.querySelector(this.options.pagination);
    
    if (!this.container || !this.pagination) {
      console.warn('Focal Infinite Scroll: Required elements not found');
      return;
    }

    this.setupIntersectionObserver();
    this.createLoader();
    
    // Hide original pagination
    this.pagination.style.opacity = '0';
    this.pagination.style.visibility = 'hidden';
    this.pagination.style.height = '0';
    this.pagination.style.overflow = 'hidden';
    
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this), { passive: true });
  }

  setupIntersectionObserver() {
    const observerOptions = {
      root: null,
      rootMargin: `${this.options.threshold}px`,
      threshold: 0.1
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isLoading && !this.maxPagesReached) {
          this.loadMore();
        }
      });
    }, observerOptions);

    this.observer.observe(this.pagination);
  }

  createLoader() {
    if (!this.options.enablePreloader) return;
    
    this.loader = document.createElement('div');
    this.loader.className = 'infinite-scroll-loader';
    this.loader.innerHTML = `
      <div class="loading-dots">
        <span></span><span></span><span></span>
      </div>
      <p class="heading heading--small">${this.options.loadingText}</p>
    `;
    this.loader.style.cssText = `
      display: none;
      text-align: center;
      padding: 2rem;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;
    
    // Insert after the product list
    this.productList.insertAdjacentElement('afterend', this.loader);
  }

  async loadMore() {
    if (this.isLoading || this.maxPagesReached) return;
    
    const nextLink = this.pagination.querySelector('a[rel="next"]');
    if (!nextLink) {
      this.maxPagesReached = true;
      return;
    }

    this.isLoading = true;
    this.showLoader();
    
    try {
      const response = await fetch(nextLink.href, {
        signal: this.controller.signal,
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const html = await response.text();
      await this.processResponse(html);
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Focal Infinite Scroll Error:', error);
        this.showError();
      }
    } finally {
      this.isLoading = false;
      this.hideLoader();
    }
  }

  async processResponse(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const newContainer = doc.querySelector(this.options.container);
    const newPagination = doc.querySelector(this.options.pagination);
    
    if (newContainer && newContainer.children.length > 0) {
      const fragment = document.createDocumentFragment();
      Array.from(newContainer.children).forEach(child => {
        fragment.appendChild(child.cloneNode(true));
      });
      
      this.container.appendChild(fragment);
      
      // Update pagination
      if (newPagination) {
        this.pagination.innerHTML = newPagination.innerHTML;
        const nextLink = this.pagination.querySelector('a[rel="next"]');
        if (nextLink) {
          this.observer.observe(this.pagination);
          this.currentPage++;
        } else {
          this.maxPagesReached = true;
        }
      } else {
        this.maxPagesReached = true;
      }
      
      // Trigger custom event for theme compatibility
      this.container.dispatchEvent(new CustomEvent('infiniteScrollLoaded', {
        detail: { page: this.currentPage, productsLoaded: fragment.children.length }
      }));
      
      // Reinitialize theme JavaScript for new products
      if (window.theme && typeof window.theme.initProductItems === 'function') {
        window.theme.initProductItems();
      }
      
      // Trigger stagger animation for new products if enabled
      if (this.productList.hasAttribute('stagger-apparition')) {
        const newProducts = Array.from(fragment.children);
        newProducts.forEach((product, index) => {
          product.style.animationDelay = `${index * 0.1}s`;
        });
      }
      
    } else {
      this.maxPagesReached = true;
    }
    
    if (this.currentPage >= this.options.maxPages) {
      this.maxPagesReached = true;
    }
  }

  showLoader() {
    if (this.loader) {
      this.loader.style.display = 'block';
      this.loader.offsetHeight; // Force reflow
      this.loader.style.opacity = '1';
    }
  }

  hideLoader() {
    if (this.loader) {
      this.loader.style.opacity = '0';
      setTimeout(() => {
        if (this.loader) this.loader.style.display = 'none';
      }, 300);
    }
  }

  showError() {
    if (this.loader) {
      this.loader.innerHTML = '<p class="heading heading--small">Unable to load more products. Please try again.</p>';
      this.loader.style.opacity = '1';
    }
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.controller.abort();
      this.controller = new AbortController();
    }
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.controller.abort();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }
}

// CSS for loading animation and Focal theme compatibility
const style = document.createElement('style');
style.textContent = `
  .loading-dots {
    display: inline-block;
    margin-bottom: 1rem;
  }
  .loading-dots span {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: rgb(var(--text-color));
    margin: 0 2px;
    animation: loading-dots 1.4s infinite both;
  }
  .loading-dots span:nth-child(1) { animation-delay: -0.32s; }
  .loading-dots span:nth-child(2) { animation-delay: -0.16s; }
  @keyframes loading-dots {
    0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
    40% { transform: scale(1); opacity: 1; }
  }
  
  .infinite-pagination {
    transition: all 0.3s ease;
  }
  
  /* Ensure smooth transitions for new products */
  .product-item {
    opacity: 0;
    animation: fadeInUp 0.6s ease forwards;
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
document.head.appendChild(style);

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('#product-grid')) {
      window.focalInfiniteScroll = new FocalInfiniteScroll();
    }
  });
} else {
  if (document.querySelector('#product-grid')) {
    window.focalInfiniteScroll = new FocalInfiniteScroll();
  }
}
